import { useEffect, useState } from 'react'

import { useDebounce } from './useDebounce'
import { usePermissions } from '@/hooks/usePermissions'
import { captureException } from '@sentry/nextjs'

import { useApolloClient } from '@apollo/client'

import { useUpdateDeviceMutation } from '@/graphql/generated/hooks'
import { UpdateDeviceInput } from '@/graphql/generated/schemas'

import { DeviceI } from '../types/types'

const REFETCH_QUERIES = ['FloorDevicesCount']

export const useUpdateDevice = (floorId: string) => {
  const { shouldEnableUpdateDevice } = usePermissions()
  const client = useApolloClient()

  const [update, { error }] = useUpdateDeviceMutation()

  const [updateInput, setUpdateInput] = useState<UpdateDeviceInput>(null)
  const debouncedUpdateInput = useDebounce(updateInput, 1500)

  const buildUpdateInput = (
    newData: DeviceI,
    devicePositionValid: boolean
  ): UpdateDeviceInput => ({
    id: newData.id,
    x: devicePositionValid ? newData.x : null,
    y: devicePositionValid ? newData.y : null,
    bearing: newData.rotation,
    fieldOfView: newData.angle,
    visibilityRange: newData.distance,
    floorId: floorId,
  })

  const updateDevice = (
    newData: DeviceI,
    devicePositionValid: boolean,
    skipDebounce?: boolean
  ) => {
    if (!shouldEnableUpdateDevice) return
    const mutationInput = buildUpdateInput(newData, devicePositionValid)

    if (skipDebounce) {
      update({
        variables: {
          input: mutationInput,
        },
        refetchQueries: REFETCH_QUERIES,
      })
    } else {
      setUpdateInput(mutationInput)
    }
  }

  /**
   * Persists positions for several devices at once. There is no bulk device mutation, so this
   * fires one `UpdateDevice` per device.
   *
   * Deliberately bypasses the debounced path above: that path holds a single pending input, so
   * it can only ever persist one device. Refetching happens once after every write settles
   * rather than per mutation, since mutations do not necessarily resolve in the order issued
   * and a mid-flight refetch would read a short count.
   *
   * Never rejects — callers are not expected to await it. Resolves `true` when at least one
   * device failed to persist.
   */
  const updateDevicePositions = async (
    newDataList: DeviceI[]
  ): Promise<boolean> => {
    if (!shouldEnableUpdateDevice || newDataList.length === 0) return false

    const results = await Promise.allSettled(
      newDataList.map((newData) =>
        update({ variables: { input: buildUpdateInput(newData, true) } })
      )
    )

    results.forEach((result) => {
      if (result.status === 'rejected') {
        captureException(result.reason)
      }
    })

    if (results.some((result) => result.status === 'fulfilled')) {
      try {
        await client.refetchQueries({ include: REFETCH_QUERIES })
      } catch (e) {
        captureException(e)
      }
    }

    return results.some((result) => result.status === 'rejected')
  }

  useEffect(() => {
    if (!debouncedUpdateInput) return
    update({
      variables: {
        input: debouncedUpdateInput,
      },
      refetchQueries: REFETCH_QUERIES,
    })
  }, [debouncedUpdateInput])

  return { updateDevice, updateDevicePositions, error }
}
